/**
 * GDPR consent state helper.
 *
 * Categories:
 *   - necessary   → always true (technical/auth cookies)
 *   - analytical  → tracking, visitor_id, UTM attribution
 *   - marketing   → future-proof (ads pixels)
 *
 * Consent is stored in localStorage as JSON { necessary, analytical, marketing,
 * timestamp, version } and is considered valid for CONSENT_TTL_DAYS.
 *
 * A consent-change event is dispatched on window when state is updated so
 * long-lived components (trackers) can react without a page reload.
 */

const KEY = "sm_consent_v1";
const EVENT = "sm:consent-changed";
const TTL_DAYS = 365;

const DEFAULT_STATE = Object.freeze({
  necessary: true,
  analytical: false,
  marketing: false,
  timestamp: 0,
  version: 1,
});

function _now() { return Date.now(); }

export function getConsent() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.timestamp) return null;
    // Expire after TTL
    if (_now() - parsed.timestamp > TTL_DAYS * 24 * 60 * 60 * 1000) {
      localStorage.removeItem(KEY);
      return null;
    }
    return { ...DEFAULT_STATE, ...parsed };
  } catch {
    return null;
  }
}

export function hasMadeChoice() {
  return getConsent() !== null;
}

export function canTrackAnalytics() {
  const c = getConsent();
  return !!(c && c.analytical);
}

export function canTrackMarketing() {
  const c = getConsent();
  return !!(c && c.marketing);
}

export function saveConsent({ analytical = false, marketing = false } = {}) {
  const state = {
    necessary: true,
    analytical: !!analytical,
    marketing: !!marketing,
    timestamp: _now(),
    version: 1,
  };
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
    window.dispatchEvent(new CustomEvent(EVENT, { detail: state }));
  } catch { /* ignore */ }
  return state;
}

export function acceptAll() {
  return saveConsent({ analytical: true, marketing: true });
}

export function rejectAll() {
  return saveConsent({ analytical: false, marketing: false });
}

export function resetConsent() {
  try {
    localStorage.removeItem(KEY);
    window.dispatchEvent(new CustomEvent(EVENT, { detail: null }));
  } catch { /* ignore */ }
}

export const CONSENT_EVENT = EVENT;
